import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import {
  ArrowRight, ArrowLeft, Check, FolderKanban, Users, Bot,
  Film, Briefcase, Camera, Palette, Loader2, ChevronRight, SkipForward
} from 'lucide-react';
import SlateLogo from './SlateLogo';
import { authHeaders } from '../contexts/AuthContext';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { id: 'welcome', title: 'Welcome to Slate' },
  { id: 'profile', title: 'Tell us about you' },
  { id: 'project', title: 'Create your first project' },
  { id: 'tour', title: 'You\'re all set' },
];

const ROLES = [
  { label: 'Producer', icon: Briefcase },
  { label: 'Director', icon: Film },
  { label: 'Cinematographer', icon: Camera },
  { label: 'Editor', icon: Palette },
  { label: 'Designer', icon: Palette },
  { label: 'Writer', icon: Film },
  { label: 'Manager', icon: Briefcase },
  { label: 'Freelancer', icon: Users },
  { label: 'Other', icon: Users },
];

const PROJECT_TYPES = ['Feature Film', 'Ad Campaign', 'Music Video', 'Product Design', 'Brand Launch', 'Documentary', 'Wedding Film', 'Web Series'];

export default function OnboardingFlow({ user, onComplete }) {
  const [step, setStep] = useState(0);
  const [profile, setProfile] = useState({ name: user?.name || '', roles: [], organization: '' });
  const [project, setProject] = useState({ name: '', project_type: 'Feature Film', description: '', budget: '' });
  const [creating, setCreating] = useState(false);
  const [createdProject, setCreatedProject] = useState(null);
  const navigate = useNavigate();

  const next = () => setStep(s => Math.min(s + 1, STEPS.length - 1));
  const prev = () => setStep(s => Math.max(s - 1, 0));

  const toggleRole = (role) => {
    setProfile(p => ({
      ...p,
      roles: p.roles.includes(role) ? p.roles.filter(r => r !== role) : [...p.roles, role]
    }));
  };

  const createProject = async () => {
    if (!project.name.trim()) return;
    setCreating(true);
    try {
      const res = await fetch(`${API}/projects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          ...project,
          budget: Number(project.budget) || 0,
          tags: [],
        }),
      });
      if (res.ok) {
        const p = await res.json();
        setCreatedProject(p);
        next();
      }
    } catch (err) { console.error('Failed to create onboarding project:', err); } finally { setCreating(false); }
  };

  const finishOnboarding = async () => {
    try {
      await fetch(`${API}/onboarding/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ name: profile.name, roles: profile.roles, organization: profile.organization }),
      });
    } catch (err) { console.error('Failed to complete onboarding:', err); }
    onComplete();
  };

  const skipOnboarding = async () => {
    await finishOnboarding();
    navigate('/dashboard');
  };

  const stepContent = {
    welcome: (
      <div className="text-center max-w-md mx-auto" data-testid="onboarding-welcome">
        <div className="mx-auto mb-6">
          <SlateLogo size={48} className="mx-auto" />
        </div>
        <h2 className="font-heading text-2xl sm:text-3xl font-medium tracking-tight mb-3">
          Welcome{user?.name ? `, ${user.name.split(' ')[0]}` : ''}
        </h2>
        <p className="text-sm text-muted-foreground leading-relaxed mb-8">
          Slate is your creative project operating system. Manage productions, find collaborators, and use AI to work smarter. Let's get you set up in under a minute.
        </p>
        <div className="grid grid-cols-3 gap-3 mb-8">
          {[
            { icon: FolderKanban, label: 'Projects' },
            { icon: Users, label: 'People' },
            { icon: Bot, label: 'AI Assistant' },
          ].map(f => (
            <div key={f.label} className="p-3 rounded-xl border border-border/50 bg-card">
              <f.icon className="w-4 h-4 text-muted-foreground mx-auto mb-1.5" strokeWidth={2} />
              <p className="text-[10px] text-muted-foreground">{f.label}</p>
            </div>
          ))}
        </div>
        <Button onClick={next} className="h-10 px-6 gap-2" data-testid="onboarding-start-btn">
          Let's go <ArrowRight className="w-4 h-4" />
        </Button>
      </div>
    ),

    profile: (
      <div className="max-w-md mx-auto" data-testid="onboarding-profile">
        <h2 className="font-heading text-2xl font-medium tracking-tight mb-2">Tell us about you</h2>
        <p className="text-sm text-muted-foreground mb-6">This helps us personalize your experience.</p>
        <div className="space-y-5">
          <div>
            <Label className="text-xs">Your name</Label>
            <Input
              value={profile.name}
              onChange={e => setProfile({ ...profile, name: e.target.value })}
              placeholder="e.g., Aarav Kumar"
              className="mt-1.5 h-10"
              data-testid="onboarding-name-input"
            />
          </div>
          <div>
            <Label className="text-xs mb-2 block">Your role(s) <span className="text-muted-foreground/60">— select all that apply</span></Label>
            <div className="flex flex-wrap gap-2" data-testid="onboarding-roles-grid">
              {ROLES.map(r => (
                <button
                  key={r.label}
                  type="button"
                  onClick={() => toggleRole(r.label)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[13px] border transition-all duration-150
                    ${profile.roles.includes(r.label)
                      ? 'bg-foreground text-background border-foreground font-medium'
                      : 'bg-transparent border-border text-muted-foreground hover:border-foreground/40 hover:text-foreground'}
                  `}
                  data-testid={`role-chip-${r.label.toLowerCase()}`}
                >
                  <r.icon className="w-3 h-3" strokeWidth={2} />
                  {r.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs">Organization <span className="text-muted-foreground">(optional)</span></Label>
            <Input
              value={profile.organization}
              onChange={e => setProfile({ ...profile, organization: e.target.value })}
              placeholder="Studio, agency, or company name"
              className="mt-1.5 h-10"
              data-testid="onboarding-org-input"
            />
          </div>
        </div>
        <div className="flex items-center gap-3 mt-8">
          <Button variant="ghost" size="sm" onClick={prev} className="gap-1"><ArrowLeft className="w-3.5 h-3.5" /> Back</Button>
          <Button onClick={next} className="flex-1 h-10 gap-2" disabled={profile.roles.length === 0} data-testid="onboarding-profile-next">
            Continue <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    ),

    project: (
      <div className="max-w-md mx-auto" data-testid="onboarding-project">
        <h2 className="font-heading text-2xl font-medium tracking-tight mb-2">Create your first project</h2>
        <p className="text-sm text-muted-foreground mb-6">Don't worry, you can always edit this later.</p>
        <div className="space-y-4">
          <div>
            <Label className="text-xs">Project name</Label>
            <Input
              value={project.name}
              onChange={e => setProject({ ...project, name: e.target.value })}
              placeholder="e.g., My Feature Film"
              className="mt-1.5 h-10"
              data-testid="onboarding-project-name"
            />
          </div>
          <div>
            <Label className="text-xs">Type</Label>
            <Select value={project.project_type} onValueChange={v => setProject({ ...project, project_type: v })}>
              <SelectTrigger className="mt-1.5 h-10" data-testid="onboarding-project-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PROJECT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Brief description <span className="text-muted-foreground">(optional)</span></Label>
            <Textarea
              value={project.description}
              onChange={e => setProject({ ...project, description: e.target.value })}
              placeholder="What's this project about?"
              className="mt-1.5"
              rows={3}
              data-testid="onboarding-project-desc"
            />
          </div>
          <div>
            <Label className="text-xs">Estimated budget <span className="text-muted-foreground">(optional)</span></Label>
            <Input
              type="number"
              value={project.budget}
              onChange={e => setProject({ ...project, budget: e.target.value })}
              placeholder="In INR"
              className="mt-1.5 h-10"
              data-testid="onboarding-project-budget"
            />
          </div>
        </div>
        <div className="flex items-center gap-3 mt-8">
          <Button variant="ghost" size="sm" onClick={prev} className="gap-1"><ArrowLeft className="w-3.5 h-3.5" /> Back</Button>
          <Button onClick={createProject} className="flex-1 h-10 gap-2" disabled={!project.name.trim() || creating} data-testid="onboarding-create-project-btn">
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <FolderKanban className="w-4 h-4" />}
            Create Project
          </Button>
        </div>
      </div>
    ),

    tour: (
      <div className="text-center max-w-md mx-auto" data-testid="onboarding-tour">
        <div className="w-12 h-12 rounded-2xl bg-foreground flex items-center justify-center mx-auto mb-6">
          <Check className="w-6 h-6 text-background" strokeWidth={2} />
        </div>
        <h2 className="font-heading text-2xl sm:text-3xl font-medium tracking-tight mb-3">You're all set!</h2>
        <p className="text-sm text-muted-foreground mb-8">
          {createdProject ? `"${createdProject.name}" has been created. ` : ''}
          Here's what you can explore next:
        </p>
        <div className="space-y-2 text-left mb-8">
          {[
            { icon: Users, label: 'Find collaborators', desc: 'Browse 25+ verified professionals on the People page', action: '/people' },
            { icon: Bot, label: 'Ask Specter', desc: 'Generate budgets, timelines, call sheets with AI', action: '/spectre' },
            { icon: FolderKanban, label: 'Manage tasks', desc: 'Add tasks, milestones and track progress', action: createdProject ? `/projects/${createdProject.project_id}` : '/projects' },
          ].map(item => (
            <button
              key={item.label}
              onClick={() => { finishOnboarding(); navigate(item.action); }}
              className="w-full flex items-center gap-4 p-4 rounded-xl border border-border/50 bg-card hover:border-border transition-all text-left group"
              data-testid={`tour-${item.label.toLowerCase().replace(/\s/g, '-')}`}
            >
              <div className="w-9 h-9 rounded-lg bg-secondary flex items-center justify-center flex-shrink-0 group-hover:bg-foreground group-hover:text-background transition-colors">
                <item.icon className="w-4 h-4" strokeWidth={2} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">{item.label}</p>
                <p className="text-xs text-muted-foreground">{item.desc}</p>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground flex-shrink-0" strokeWidth={2} />
            </button>
          ))}
        </div>
        <Button onClick={() => { finishOnboarding(); navigate('/dashboard'); }} variant="outline" size="sm" className="h-9" data-testid="skip-to-dashboard">
          Skip to Dashboard
        </Button>
      </div>
    ),
  };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-6" data-testid="onboarding-flow">
      {/* Skip button - visible on all steps except the final tour */}
      {step < STEPS.length - 1 && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="absolute top-6 right-6">
          <Button
            variant="ghost"
            size="sm"
            className="gap-1.5 text-muted-foreground hover:text-foreground"
            onClick={skipOnboarding}
            data-testid="onboarding-skip-btn"
          >
            <SkipForward className="w-3.5 h-3.5" strokeWidth={2} /> Skip
          </Button>
        </motion.div>
      )}

      {/* Progress */}
      <div className="w-full max-w-md mb-8">
        <div className="flex items-center gap-1 mb-2">
          {STEPS.map((s, i) => (
            <div
              key={s.id}
              className={`h-1 flex-1 rounded-full transition-all duration-300 ${i <= step ? 'bg-foreground' : 'bg-border'}`}
            />
          ))}
        </div>
        <p className="text-[10px] text-muted-foreground">Step {step + 1} of {STEPS.length}</p>
      </div>

      {/* Step Content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.2 }}
          className="w-full max-w-lg"
        >
          {stepContent[STEPS[step].id]}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
